import clsx from "clsx";
import Modal from "../Modal/Modal";
import { MdCheck, MdClose } from "react-icons/md";
import { ETransactionType, ICON_SIZES } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import TextInput from "../TextInput/TextInput";
import TextAreaInput from "../TextAreaInput/TextAreaInput";
import RecurringTransactionAttachments from "./RecurringTransactionAttachments";
import { useEffect, useMemo, useRef, useState } from "react";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import CustomSelect from "../Select/CustomSelect";
import { v4 as uuidv4 } from "uuid";

import {
  calculateNextPaymentDate,
  formatFileSize,
  showNotification,
  transformToSelectOptions,
} from "src/helpers/utils";
import useCurrentUser from "src/hooks/useCurrentUser";
import useCollapsibles, {
  ERecurringTransactionMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import useMobile from "src/hooks/useMobile";
import moment from "moment";
import { ERecurringTransactionFrequency } from "./RecurringTransactionSettings";
import TransactionAttachmentsLightbox from "src/pages/NewTransaction/TransactionAttachmentsLightbox";
import { addTransaction, updateTransaction } from "src/api/transactions";
import { TRANSACTION_STATUS_PAID } from "src/constants/transaction-status";
import useCache from "src/hooks/useCache";
import { capitalize, omit } from "lodash";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { EButtonKind } from "../Button/ButtonTypes";

export interface IRecurringTransactionPayment {
  onClose: () => void;
  transaction: any;
}

const RecurringTransactionPayment: React.FC<IRecurringTransactionPayment> = ({
  onClose,
  transaction,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const thumbnailsRef = useRef<any>(null);
  const { clearCache } = useCache();
  const [isSavingPayment, setIsSavingPayment] = useState<boolean>(false);
  const transactionType =
    transaction?.type === ETransactionType.INCOME ? "ingreso" : "gasto";

  const [notes, setNotes] = useState<string>("");
  const [method, setMethod] = useState<string>(transaction?.paymentMethod);
  const [amount, setAmount] = useState<string>(transaction?.total);
  const [date, setDate] = useState<string>(moment().format("yyyy-MM-DD"));
  const [nextPayment, setNextPayment] = useState<string>("");
  const {
    isRecurringTransactionMobileCollapsibleOpen,
    toggleRecurringTransactionMobileCollapsible,
    updateCollapsible,
    collapsiblePreferences,
  } = useCollapsibles();

  const [attachmentsGalleryIsOpen, setAttachmentsGalleryIsOpen] =
    useState<boolean>(false);
  const [clickedAttachmentIndex, setClickedAttachmentIndex] =
    useState<number>(0);
  const [showConfirmPayment, setShowConfirmPayment] = useState<boolean>(false);

  const { allPaymentMethods, allEnabledPaymentMethods } = useCurrentUser();
  const paymentMethods = useMemo(() => {
    return transformToSelectOptions({
      data: allEnabledPaymentMethods,
      labelField: "name",
      valueField: "id",
    });
  }, [allEnabledPaymentMethods]);

  const [filesToAttach, setFilesToAttach] = useState<Array<any>>([]);

  const saveIsEnabled = useMemo(() => {
    return date && amount && method && nextPayment;
  }, [date, amount, method, nextPayment]);

  const unifiedAttachmentsList = useMemo(() => {
    return [
      ...(filesToAttach?.map?.((theFile: any) => {
        return {
          name: theFile.name,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() || "Desconocido",
          isSaved: false,
          thumbnailUrl: theFile?.preview,
          fileUrl: theFile?.preview,
        };
      }) || []),
    ];
  }, [filesToAttach]);

  const attachmentSlides: any[] = useMemo(() => {
    return (
      unifiedAttachmentsList?.map?.((attachment: any) => {
        return {
          type: attachment?.type === "application/pdf" ? undefined : "image",
          src: attachment?.fileUrl,
        };
      }) || []
    );
  }, [unifiedAttachmentsList]);

  useEffect(() => {
    if (date !== ERecurringTransactionFrequency.CUSTOM) {
      const newDate = calculateNextPaymentDate(
        date,
        transaction?.recurring?.settings?.frequency
      );
      setNextPayment(newDate);
    }
  }, [date, transaction?.recurring?.settings?.frequency]);

  const savePayment = async () => {
    try {
      setIsSavingPayment(true);
      const newTransactionId = uuidv4();
      const cleanOriginal = omit(transaction, [
        "_id",
        "invoice",
        "recurring",
        "deleted",
        "archived",
        "shared",
        "createdDate",
        "sharedBy",
        "attachments",
      ]);

      const res = await addTransaction({
        transactionId: newTransactionId,
        fieldsToSave: {
          ...cleanOriginal,
          invoice: newTransactionId,
          date,
          documentNumber: "",
          subTotal: amount,
          status: TRANSACTION_STATUS_PAID,
          paymentMethod: method,
          total: amount,
          origin: {
            from: transaction?.invoice,
            type: "RECURRING",
          },
        },
        files: filesToAttach,
      });

      if (res?.success) {
        await updateTransaction({
          transactionId: transaction?.invoice,
          fieldsToUpdate: {
            recurring: {
              ...transaction?.recurring,
              nextPayment: nextPayment,
            },
          },
        });
        await clearCache();

        onClose();
        showNotification({
          kind: "success",
          message: `${capitalize(transactionType)} registrado correctamente`,
        });
      } else {
        showNotification({
          message: `Ocurrió un error al registrar el ${transactionType}`,
          kind: "error",
        });
      }
    } catch (e) {
      showNotification({
        message: `Ocurrió un error al registrar el ${transactionType}`,
        kind: "error",
      });
    } finally {
      setIsSavingPayment(false);
    }
  };

  return (
    <>
      <Modal
        onEscape={() => {
          if (!isSavingPayment) onClose();
        }}
        className="md:!max-w-[700px] md:!min-w-[700px] md:!w-full"
      >
        <Modal.Header
          title={`Registrar ${transactionType}`}
          onClose={() => {
            if (!isSavingPayment) onClose();
          }}
        />
        <Modal.Body>
          <div className={clsx(["flex", "flex-col", "gap-6", "pb-2"])}>
            <CollapsibleBlock
              title="Detalles"
              open={
                isMobile
                  ? isRecurringTransactionMobileCollapsibleOpen(
                      ERecurringTransactionMobileCollapsibles.DETAILS
                    )
                  : collapsiblePreferences.recurringTransactionDetailsOpen
              }
              onChange={(isOpen: boolean) => {
                if (isMobile) {
                  toggleRecurringTransactionMobileCollapsible(
                    ERecurringTransactionMobileCollapsibles.DETAILS
                  );
                } else {
                  updateCollapsible({
                    recurringTransactionDetailsOpen: isOpen,
                  });
                }
              }}
            >
              <div className={clsx(["flex", "flex-col", "gap-6"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-2",
                    "md:grid-cols-2",
                    "gap-4",
                  ])}
                >
                  <TextInput
                    value={date}
                    onChange={(e: any) => setDate(e?.target?.value)}
                    label="Fecha"
                    type="date"
                    required
                  />
                  <TextInput
                    value={amount}
                    onChange={(e: any) => setAmount(e?.target?.value)}
                    label="Monto"
                    placeholder="Monto de la transacción"
                    type="number"
                    required
                  />
                </div>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-2",
                    "gap-6",
                    "md:gap-4",
                  ])}
                >
                  <CustomSelect
                    options={paymentMethods}
                    onChange={(e: any) => setMethod(e?.value)}
                    label="Método de pago"
                    value={method}
                    placeholder={isMobile ? "Seleccionar" : "Método de pago"}
                    useValueAsLabel={false}
                    required
                  />
                  <TextInput
                    value={nextPayment}
                    onChange={(e: any) => setNextPayment(e?.target?.value)}
                    label="Fecha de la próxima transacción"
                    type="date"
                    required
                  />
                </div>
                <TextAreaInput
                  placeholder={t("Notas")}
                  onChange={(e) => setNotes(e?.target?.value)}
                  value={notes}
                  rows={3}
                  label="Notas"
                />
              </div>
            </CollapsibleBlock>

            <RecurringTransactionAttachments
              attachments={[]}
              onChange={({ unsaved }) => setFilesToAttach(unsaved)}
              onClick={({
                saved,
                index,
              }: {
                saved: boolean;
                index: number;
              }) => {
                setClickedAttachmentIndex(index);
                setAttachmentsGalleryIsOpen(true);
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          actions={[
            {
              text: t("shared.save"),
              onClick: () => setShowConfirmPayment(true),
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.MD} />,
              disabled: !saveIsEnabled || isSavingPayment,
              isLoading: isSavingPayment,
            },
            {
              text: t("shared.close"),
              onClick: () => {
                if (!isSavingPayment) onClose();
              },
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.MD} />,
              disabled: isSavingPayment,
            },
          ]}
        />
      </Modal>
      {attachmentsGalleryIsOpen && (
        <TransactionAttachmentsLightbox
          slides={attachmentSlides}
          index={clickedAttachmentIndex}
          setIndex={setClickedAttachmentIndex}
          thumbnailsRef={thumbnailsRef}
          unifiedAttachmentsList={unifiedAttachmentsList}
          onClose={() => setAttachmentsGalleryIsOpen(false)}
        />
      )}
      {showConfirmPayment && (
        <ConfirmDialog
          description={`¿Deseas guardar el ${transactionType}?`}
          onClose={() => {
            if (!isSavingPayment) {
              setShowConfirmPayment(false);
            }
          }}
          title={`Guardar`}
          isLoading={isSavingPayment}
          onConfirm={async () => {
            await savePayment();

            setShowConfirmPayment(false);
          }}
        />
      )}
    </>
  );
};

export default RecurringTransactionPayment;
